import { Box, Button, DebouncedTextField, Divider, Flex, Menu, Text, toaster } from 'components'
import { useWorkspace } from 'components/providers'
import { startCase } from 'lodash-es'
import { useNavigate } from 'react-router'
import { WorkspaceStatusValue } from 'types'
import {
  createTimestamp,
  deleteUnusedContent,
  forceLight,
  useBreakpoint,
  useEntevatePermission,
  useTheme,
} from 'utils'
import ManageFollowUpGroups from './ManageFollowUpGroups'

export interface Props {
  onDeleted?: () => void
  onRefresh?: () => void
}

export default function WorkspaceSettings({ onDeleted, onRefresh }: Props) {
  const [entevatePermission] = useEntevatePermission()
  const { updateWorkspace, workspace } = useWorkspace()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const isMobile = useBreakpoint() < 1

  if (!workspace) return null
  const { id, title } = workspace

  async function updateTitle(title: string) {
    await updateWorkspace({ title })
    onRefresh?.()
  }

  async function handleDeleteUnusedContent() {
    const deletePromise = deleteUnusedContent({ workspaceId: id }).then(res => res.data)
    toaster.promise(deletePromise, {
      loading: {
        title: 'Deleting Unused Content',
        description: 'Analyzing and removing unused content...',
      },
      success: data => ({
        closable: true,
        title: 'Unused Content Deleted',
        duration: 7000,
        description: `Deleted: ${data.deleted.media} media, ${data.deleted.text} text, ${data.deleted.tools} tools, ${data.deleted.traits2} traits.`,
      }),
      error: {
        title: 'Delete Failed',
        description: 'Failed to delete unused content. Please try again.',
      },
    })

    await deletePromise
  }

  async function handleStatusChange(status: WorkspaceStatusValue) {
    await updateWorkspace({ status: { value: status, updated: createTimestamp() } })
    if (status === 'archived') onDeleted?.()
    navigate('/', { replace: true })
  }

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Text textStyle="h3" pt={2}>
        Workspace Settings
      </Text>
      <Box
        {...forceLight}
        display="flex"
        bg="linear-gradient(167deg, rgba(255, 255, 255, 0.70) 2.36%, rgba(255, 255, 255, 0.60) 92.17%)"
        px={{ base: 4, sm: 14 }}
        py={10}
        gap={5}
        borderRadius="xl"
        border="1px solid #FFF"
        boxShadow="0px 7px 16px 0px rgba(0, 0, 0, 0.08)"
        alignItems="flex-start"
        flexDirection="column"
        backdropFilter="blur(40px)"
        justifyContent="center"
      >
        <DebouncedTextField
          autoFocus
          label="Workspace Name"
          key={id}
          debounceTime={500}
          onChange={updateTitle}
          placeholder="Title"
          showStatus
          value={title}
          bg="#fff"
          backdropFilter="blur(27px)"
          borderRadius="10px"
          border="1px solid rgba(23, 23, 23, 0.20)"
        />

        <ManageFollowUpGroups />

        {entevatePermission === 'entevate' && (
          <Flex direction={'column'} w={{ base: '100%', sm: '70%' }} gap={5}>
            <Divider
              borderColor={theme === 'light' ? 'rgba(0, 0, 0, 0.10)' : 'rgba(255, 255, 255, 0.60)'}
            />

            <Flex
              align="center"
              backdropFilter="blur(27px)"
              bg="#fff"
              border="1px solid rgba(23, 23, 23, 0.20)"
              borderRadius="10px"
              direction={{ base: 'column', sm: 'row' }}
              gap={2}
              justify="space-between"
              p={5}
            >
              <Flex direction="column" gap={1}>
                <Text textStyle="bodyMedium">Workspace Status</Text>
                <Text textStyle="bodySmall" fontWeight="normal">
                  Change the status of this workspace. Archived workspaces are hidden from the
                  workspace list. Inactive workspaces limit users to read only.
                </Text>
              </Flex>
              <Menu
                value={workspace.status.value}
                onChange={handleStatusChange}
                portal
                options={(['active', 'inactive', 'archived'] as WorkspaceStatusValue[]).map(
                  value => ({
                    value,
                    label: startCase(value),
                  })
                )}
              />
            </Flex>

            <Flex
              align="center"
              backdropFilter="blur(27px)"
              bg="#fff"
              border="1px solid rgba(23, 23, 23, 0.20)"
              borderRadius="10px"
              direction={{ base: 'column', sm: 'row' }}
              gap={2}
              justify="space-between"
              p={5}
            >
              <Flex direction="column" gap={1}>
                <Text textStyle="bodyMedium">Delete Unused Content</Text>
                <Text textStyle="bodySmall" fontWeight="normal">
                  Remove unused media, text, tools, traits, and trait groups from this workspace.
                </Text>
              </Flex>
              <Button
                p={5}
                color="white"
                bg="warning.500"
                borderRadius="md"
                onClick={handleDeleteUnusedContent}
                _hover={{ bg: undefined }}
              >
                Delete Unused Content
              </Button>
            </Flex>
          </Flex>
        )}
      </Box>
    </Flex>
  )
}
